import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { NGXLogger } from 'ngx-logger';
import { omit } from 'remeda';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { shareReplay, throttleTime } from 'rxjs/operators';
import { SnackBarService } from '../shared/snackbar.service';
import { LeaderboardForAdmin, LeaderboardPoolDriversGroupResponse, LeaderboardPoolForAdmin } from '../types';

@Injectable({
  providedIn: 'root',
})
export class LeaderboardService {
  private loadingForPool$$ = new BehaviorSubject<Record<string, boolean>>({});
  public loadingForPool$ = this.loadingForPool$$.asObservable().pipe(shareReplay(1));

  private leaderboardPools$$ = new BehaviorSubject<LeaderboardPoolForAdmin[]>(null);
  private leaderboardPoolsShared$ = this.leaderboardPools$$.asObservable().pipe(shareReplay(1));
  public get leaderboardPools$(): Observable<LeaderboardPoolForAdmin[]> {
    return this.leaderboardPoolsShared$;
  }

  private poolDrivers$$ = new BehaviorSubject<LeaderboardPoolDriversGroupResponse>(null);
  private poolDriversThrottle$$ = new Subject();
  private poolDriversShared$ = this.poolDrivers$$.asObservable().pipe(shareReplay(1));
  public get poolDrivers$(): Observable<LeaderboardPoolDriversGroupResponse> {
    this.poolDriversThrottle$$.next();
    return this.poolDriversShared$;
  }

  constructor(private httpClient: HttpClient, private logger: NGXLogger, private snackbar: SnackBarService) {
    this.loadLeaderboardPools();

    this.poolDriversThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadLeaderboardPoolDrivers();
    });
  }

  public async loadLeaderboardForPool(poolId: number, leaderboardId: number) {
    if (this.loadingForPool$$[poolId]) {
      return;
    }
    const poolIndex = this.leaderboardPools$$.value?.findIndex((pool) => pool.id === poolId);
    if (poolIndex === -1) {
      return;
    }
    this.loadingForPool$$.next({
      ...this.loadingForPool$$.value,
      [`${poolId}`]: true,
    });

    try {
      const result = await this.httpClient
        .get<{ leaderboard: LeaderboardForAdmin }>(`${environment.api}/v1/admin/leaderboard/${leaderboardId}`)
        .toPromise();

      if (!result.leaderboard) {
        return;
      }

      const existingPool = this.leaderboardPools$$.value[poolIndex];
      if (!existingPool) {
        return;
      }

      const copyOfPool: LeaderboardPoolForAdmin = {
        ...existingPool,
        leaderboard: result.leaderboard,
      };

      const copyOfPools = [...this.leaderboardPools$$.value];
      copyOfPools[poolIndex] = copyOfPool;
      this.leaderboardPools$$.next(copyOfPools);
    } catch (error) {
      this.logger.error(error);
    } finally {
      this.loadingForPool$$.next(omit(this.loadingForPool$$.value, [`${poolId}`]));
    }
  }

  public async moveDriverToPool(poolId: number, driverId: string) {
    try {
      const result = await this.httpClient
        .post<LeaderboardPoolDriversGroupResponse>(`${environment.api}/v1/admin/leaderboard/swap`, {
          poolId,
          driverId,
        })
        .toPromise();
      this.poolDrivers$$.next(result);
    } catch (error) {
      this.logger.error(error);
      this.snackbar.showError('failed to move driver, refreshing the driver list');
      this.loadLeaderboardPoolDrivers();
    }
  }

  private async loadLeaderboardPools() {
    try {
      const result = await this.httpClient
        .get<{ leaderboardPools: LeaderboardPoolForAdmin[] }>(`${environment.api}/v1/admin/leaderboard`)
        .toPromise();
      this.leaderboardPools$$.next(result.leaderboardPools);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadLeaderboardPoolDrivers() {
    try {
      const result = await this.httpClient
        .get<LeaderboardPoolDriversGroupResponse>(`${environment.api}/v1/admin/leaderboard/drivers`)
        .toPromise();
      this.poolDrivers$$.next(result);
    } catch (error) {
      this.logger.error(error);
    }
  }
}
